/**
 * Вкладка «Тех. операции» карточки поля (AK-736, UAT founder 21.08).
 *
 * До этой правки полный список операций поля был доступен только как
 * «легаси-подэкран» внутри «Обзора» (строка-ссылка «все →» из
 * `OperationsWidget`), рисовал сырые ISO-даты без сортировки-по-порядку и не
 * был отдельной вкладкой карточки. Founder 21.08: список должен быть
 * ПОЛНОЦЕННОЙ вкладкой с теми же датами/сортировкой, что и виджет «Обзора».
 *
 * Данные и форматирование — ТЕ ЖЕ функции, что у `OperationsWidget`
 * (`widgetLogic.ts`: `operationChip`/`operationDate`/`operationSubtitle`/
 * `formatFullDate`) и тот же хук (`useFieldOperations` →
 * `GET /fields/{id}/operations`, теперь сортирует по
 * `COALESCE(actual_start_date, planned_start_date) DESC` — см. `operations.py`
 * `FIELD_OPS_ORDER_BY`). Один источник форматирования на виджет и вкладку —
 * иначе они снова разъедутся, как разъехались дата и сортировка до этой
 * правки.
 */
import { Link } from '@tanstack/react-router';
import { Tractor } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useFieldOperations } from './widgets/widgetApi';
import {
  operationChip,
  operationDate,
  operationSubtitle,
  formatFullDate,
  type OpTone,
} from './widgets/widgetLogic';

const CHIP_CLASS: Record<OpTone, string> = {
  fact: 'bg-primary/15 text-primary hover:bg-primary/15',
  plan: 'bg-accent-brand/15 text-accent-brand hover:bg-accent-brand/15',
  cancelled: 'bg-muted text-muted-foreground hover:bg-muted',
};

interface Props {
  fieldId: string;
}

export function FieldOperationsTab({ fieldId }: Props) {
  const { data, isLoading, isError } = useFieldOperations(fieldId);

  return (
    <div className="space-y-3 py-3">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Tractor className="h-4 w-4 text-muted-foreground" />
            Тех. операции
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Все операции поля — от новых к старым.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[0, 1, 2, 3, 4].map((i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : isError ? (
            <Alert variant="destructive">
              <AlertDescription>Не удалось загрузить операции.</AlertDescription>
            </Alert>
          ) : !data || data.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              Операций по полю пока нет.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {data.map((op) => {
                const chip = operationChip(op);
                const d = operationDate(op);
                return (
                  <li key={op.id} className="flex items-center gap-3 py-2">
                    <span className="w-16 shrink-0 text-xs tabular-nums text-muted-foreground">
                      {formatFullDate(d)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <Link
                        to={`/operations/${op.id}` as string}
                        className="block truncate text-sm font-medium hover:text-primary"
                      >
                        {op.name}
                      </Link>
                      <div className="truncate text-xs text-muted-foreground">
                        {operationSubtitle(op)}
                      </div>
                    </div>
                    <Badge variant="secondary" className={`${CHIP_CLASS[chip.tone]} shrink-0`}>
                      {chip.label}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default FieldOperationsTab;
